// ./src/utils/api/RsvpQuestionConditionService.ts
//
// Visibility rules for a registration question (show it only when the rule
// matches). For now the editor manages a single `rsvp_status` condition — show
// the question only for guests whose response is one of the chosen statuses.
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';

const FIELDS = `
  id
  rsvpQuestionId
  conditionKind
  operator
  rsvpStatuses
  guestGroupId
  dependsOnQuestionId
  dependsOnOptionId
  valueText
  orderNo
  status
`;

const LIST_QUERY = gql`
  query RsvpQuestionConditionList($filter: RsvpQuestionConditionFilter, $params: PaginationAndSorting) {
    rsvpQuestionConditionList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation RsvpQuestionConditionCreate($params: RsvpQuestionConditionInput) {
    rsvpQuestionConditionCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation RsvpQuestionConditionUpdate($where: RsvpQuestionConditionWhereInput, $params: RsvpQuestionConditionInput) {
    rsvpQuestionConditionUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation RsvpQuestionConditionRemove($where: RsvpQuestionConditionWhereInput) {
    rsvpQuestionConditionRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface RsvpQuestionCondition {
  id?: string;
  rsvpQuestionId?: string | null;
  conditionKind?: string | null;
  operator?: string | null;
  rsvpStatuses?: string[] | null;
  guestGroupId?: string | null;
  dependsOnQuestionId?: string | null;
  dependsOnOptionId?: string | null;
  valueText?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface RsvpQuestionConditionInput {
  rsvpQuestionId?: string | null;
  conditionKind?: string | null;
  operator?: string | null;
  rsvpStatuses?: string[] | null;
  guestGroupId?: string | null;
  dependsOnQuestionId?: string | null;
  dependsOnOptionId?: string | null;
  valueText?: string | null;
  orderNo?: number | null;
}

export class RsvpQuestionConditionService {
  static async list(rsvpQuestionId: string): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, { filter: { rsvpQuestionId: [rsvpQuestionId] } });
  }

  static async create(params: RsvpQuestionConditionInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(id: string, params: RsvpQuestionConditionInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
